/**
 * Triagem: o equipamento que o sistema ainda não conhece.
 *
 * Quando o texto de "Equip./Fornecedor" nunca foi visto, a etapa entra sem
 * estação — a OS **não** é barrada por causa disso (RN-005). Ela aparece aqui,
 * o encarregado diz uma vez onde aquilo roda, e todas as etapas paradas com o
 * mesmo texto são classificadas de uma vez. As próximas OS já chegam certas.
 */

import type { ItemDeTriagem } from '@pcp/dados';
import { aviso, botao, cabecalhoDePagina, h, notificar, render, sf, vazio } from '@pcp/ui';
import type { Aplicacao } from '../estado.js';

export async function telaTriagem(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const itens = await app.repo.triagem();
  const cadastros = await app.repo.carregarCadastros();

  if (itens.length === 0) {
    render(destino, h('.vstack.gap-4', [
      cabecalhoDePagina({
        olho: 'Entrada de OS',
        titulo: 'Triagem de estações',
        lead: 'Equipamento que apareceu na OS e o sistema não soube em que estação encaixar. Ensinar uma vez resolve as próximas.',
      }),

      h('.cartao', vazio(
        'checkmark-circle',
        'Nada para classificar',
        'Todo equipamento que apareceu nas OS já tem estação. Quando o sistema da gráfica trouxer um nome novo, ele aparece aqui.',
      )),
      blocoRegras(app),
    ]));
    return;
  }

  render(destino, [
    h('.vstack.gap-4', [
      aviso(
        'aviso',
        `${itens.length} ${itens.length === 1 ? 'equipamento sem estação' : 'equipamentos sem estação'}. As OS entraram normalmente, mas estas etapas não aparecem em fila nenhuma até serem classificadas.`,
      ),
      ...itens.map((item) => cartaoDeTriagem(app, item, cadastros)),
      blocoRegras(app),
    ]),
  ]);
}

function cartaoDeTriagem(
  app: Aplicacao,
  item: ItemDeTriagem,
  cadastros: Awaited<ReturnType<Aplicacao['repo']['carregarCadastros']>>,
): HTMLElement {
  let estacaoId = '';
  let tipo: 'operacao' | 'custo' | 'ignorar' = 'operacao';

  const seletorEstacao = h(
    'select.mac-popup',
    {
      style: { minWidth: '260px' },
      onchange: (ev: Event) => {
        estacaoId = (ev.target as HTMLSelectElement).value;
        atualizarBotao();
      },
    },
    [
      h('option', { value: '' }, 'Escolha a estação…'),
      ...cadastros.setores.flatMap((setor) => {
        const estacoes = cadastros.estacoes.filter((e) => e.setorId === setor.id && e.ativa);
        if (estacoes.length === 0) return [];
        return [
          h('optgroup', { label: setor.nome }, estacoes.map((e) => h('option', { value: e.id }, e.nome))),
        ];
      }),
    ],
  );

  const seletorTipo = h(
    '.mac-segmented',
    [
      ['operacao', 'É uma tarefa'],
      ['custo', 'É custo'],
      ['ignorar', 'Ignorar'],
    ].map(([valor, rotulo]) =>
      h(
        `button${valor === 'operacao' ? '.is-active' : ''}`,
        {
          type: 'button',
          onclick: (ev: MouseEvent) => {
            tipo = valor as typeof tipo;
            const pai = (ev.currentTarget as HTMLElement).parentElement;
            for (const b of pai?.children ?? []) b.classList.remove('is-active');
            (ev.currentTarget as HTMLElement).classList.add('is-active');
            seletorEstacao.toggleAttribute('disabled', tipo === 'ignorar');
            atualizarBotao();
          },
        },
        rotulo,
      ),
    ),
  );

  const botaoSalvar = botao(
    'Classificar',
    async () => {
      const quantas = await app.repo.aprenderDePara({
        secao: null,
        equipamentoOs: item.equipamentoOs,
        nomeEtapa: item.equipamentoOs ? null : item.nomeOs,
        estacaoId: tipo === 'ignorar' ? null : estacaoId || null,
        tipoLinha: tipo,
        por: app.sessao.pessoaId,
      });
      notificar({
        titulo: 'Classificado',
        corpo: `${quantas} ${quantas === 1 ? 'etapa entrou' : 'etapas entraram'} na fila.`,
        icone: 'checkmark-circle',
      });
      app.redesenhar();
    },
    { estilo: 'primary', desabilitado: true },
  );

  const atualizarBotao = (): void => {
    const pronto = tipo === 'ignorar' || estacaoId !== '';
    botaoSalvar.toggleAttribute('disabled', !pronto);
  };

  return h('.cartao', [
    h('.hstack.gap-3.wrap', [
      h('.vstack', { style: { gap: '2px', flex: '1', minWidth: '240px' } }, [
        h('.hstack.gap-2', [
          h('span.t-title3', item.equipamentoOs ?? item.nomeOs),
          h('span.selo.s-cinza', `${item.etapas.length} ${item.etapas.length === 1 ? 'etapa' : 'etapas'}`),
        ]),
        h('.t-callout.t-secondary', [
          'Aparece como ',
          h('b', item.nomeOs),
          ` na seção ${rotuloSecao(item.secao)}`,
        ]),
        h('.t-subhead.t-tertiary', [
          'OS: ',
          ...item.osNumeros.slice(0, 8).map((n, i) =>
            h('a.pointer', { onclick: () => app.roteador.ir(`/os/${n}`) }, `${i > 0 ? ', ' : ''}${n}`),
          ),
          item.osNumeros.length > 8 ? ` e mais ${item.osNumeros.length - 8}` : '',
        ]),
      ]),
      h('.vstack.gap-2', [
        seletorTipo,
        h('.hstack.gap-2', [seletorEstacao, botaoSalvar]),
      ]),
    ]),
  ]);
}

function rotuloSecao(s: string): string {
  if (s === 'pre_impressao') return 'Pré-impressão';
  if (s === 'impressao') return 'Impressão';
  if (s === 'acabamento') return 'Acabamento';
  return s.replace(/_/g, ' ');
}

function blocoRegras(app: Aplicacao): HTMLElement {
  const area = h('.vstack.gap-1');
  const cartao = h('.cartao', [
    h('.hstack', [
      h('.bloco-t', 'De-para de equipamentos'),
      h('.spacer'),
      h('span.t-subhead.t-secondary', 'O que o sistema já sabe'),
    ]),
    area,
  ]);

  void (async () => {
    const c = await app.repo.carregarCadastros();
    const nomeEstacao = new Map(c.estacoes.map((e) => [e.id, e.nome]));
    render(area, [
      h('table.tab', [
        h('thead', h('tr', [
          h('th', 'Texto na OS'),
          h('th', 'Vira'),
          h('th', { style: { width: '120px' } }, 'Tipo'),
          h('th', { style: { width: '60px' } }, ''),
        ])),
        h('tbody',
          c.dePara.map((r) =>
            h('tr', [
              h('td', [
                r.equipamentoOs ? h('b', r.equipamentoOs) : null,
                r.nomeEtapa ? h('span.t-secondary', `${r.equipamentoOs ? ' · ' : ''}etapa "${r.nomeEtapa}"`) : null,
                r.aprendidoEm ? h('span.selo.s-cinza', { style: { marginLeft: '6px' } }, 'aprendido') : null,
              ]),
              h('td', r.estacaoId ? (nomeEstacao.get(r.estacaoId) ?? '—') : '—'),
              h('td', r.tipoLinha === 'custo'
                ? h('span', { style: { color: 'var(--sys-orange)' } }, 'custo')
                : r.tipoLinha === 'ignorar'
                  ? h('span.t-tertiary', 'ignorar')
                  : 'tarefa'),
              h('td', h('button.mac-tb-btn.icon', {
                type: 'button',
                'data-tooltip': 'Apagar regra',
                onclick: async () => {
                  await app.repo.apagarDePara(r.id);
                  await app.repo.reclassificarEtapas();
                  app.redesenhar();
                },
              }, sf('trash'))),
            ]),
          ),
        ),
      ]),
    ]);
  })();

  return cartao;
}
